import { useEffect, useRef } from "react";
import { useRevalidator } from "react-router";
import { useSelector } from "react-redux";
import { userAuthorizedSelector } from "@/redux/authorization/authorization.selector";

const PREVIOUS_AUTH_STATE_KEY = "fido_previous_auth_state";

/**
 * AuthRevalidator component that listens to authentication state changes
 * and triggers route revalidation to ensure loaders are re-executed.
 *
 * Uses localStorage to persist auth state across component remounts,
 * ensuring reliable logout detection even when components remount.
 */
export const AuthRevalidator = () => {
  const revalidator = useRevalidator();
  const { isAuthorized } = useSelector(userAuthorizedSelector);
  const previousAuthState = useRef<boolean | null>(null);

  useEffect(() => {
    // Get the previous auth state from localStorage
    const storedPreviousState = localStorage.getItem(PREVIOUS_AUTH_STATE_KEY);
    const previousState = storedPreviousState
      ? JSON.parse(storedPreviousState)
      : null;

    // Initialize the ref with stored state on first run
    if (previousAuthState.current === null) {
      previousAuthState.current = previousState;
    }

    // Check if auth state has changed
    const hasStateChanged =
      previousAuthState.current !== null &&
      previousAuthState.current !== isAuthorized;

    if (hasStateChanged) {
      // Trigger revalidation which will re-run all active route loaders
      revalidator.revalidate();
    }

    // Update both ref and localStorage with current state
    previousAuthState.current = isAuthorized;
    localStorage.setItem(PREVIOUS_AUTH_STATE_KEY, JSON.stringify(isAuthorized));
  }, [isAuthorized, revalidator]);

  // This component doesn't render anything visible
  return null;
};
